import {ICustomersClient} from '@/services/ICustomersClient';
import {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  CancelToken
} from 'axios';
import apiAxiosClient from '@/utils/axios';
import {Customer} from '@/interfaces/customer';
import {DataSourceResult} from '@/interfaces/dataSourceResult';
import {isAxiosError, throwException} from '@/utils/errors';

export class CustomersClient implements ICustomersClient {
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;
  private instance: AxiosInstance;
  private baseUrl: string;

  constructor(baseUrl?: string, instance?: AxiosInstance) {
    this.instance = instance ? instance : apiAxiosClient;

    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : '';
  }

  /**
   * @param request (optional)
   * @return Success
   */
  customersGET(
    request?: string | undefined,
    cancelToken?: CancelToken | undefined
  ): Promise<DataSourceResult> {
    let url_ = this.baseUrl + '/api/Customers?';
    if (request === null)
      throw new Error("The parameter 'request' cannot be null.");
    else if (request !== undefined) url_ += request + '&';
    url_ = url_.replace(/[?&]$/, '');

    const options_ = <AxiosRequestConfig>{
      method: 'GET',
      url: url_,
      headers: {
        Accept: 'application/json'
      },
      cancelToken
    };

    return this.instance
      .request(options_)
      .catch((_error: any) => {
        if (isAxiosError(_error) && _error.response) {
          return _error.response;
        } else {
          throw _error;
        }
      })
      .then((_response: AxiosResponse) => {
        return this.processCustomersGET(_response);
      });
  }

  /**
   * @param body (optional)
   * @return Success
   */
  customersPOST(
    body?: Customer | undefined,
    cancelToken?: CancelToken | undefined
  ): Promise<Customer> {
    let url_ = this.baseUrl + '/api/Customers';
    url_ = url_.replace(/[?&]$/, '');

    const content_ = JSON.stringify(body);

    const options_ = <AxiosRequestConfig>{
      data: content_,
      method: 'POST',
      url: url_,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      cancelToken
    };

    return this.instance
      .request(options_)
      .catch((_error: any) => {
        if (isAxiosError(_error) && _error.response) {
          return _error.response;
        } else {
          throw _error;
        }
      })
      .then((_response: AxiosResponse) => {
        return this.processCustomersPOST(_response);
      });
  }

  /**
   * @return Success
   */
  customersGET2(
    id: number,
    cancelToken?: CancelToken | undefined
  ): Promise<Customer> {
    let url_ = this.baseUrl + '/api/Customers/{id}';
    if (id === undefined || id === null)
      throw new Error("The parameter 'id' must be defined.");
    url_ = url_.replace('{id}', encodeURIComponent('' + id));
    url_ = url_.replace(/[?&]$/, '');

    const options_ = <AxiosRequestConfig>{
      method: 'GET',
      url: url_,
      headers: {
        Accept: 'application/json'
      },
      cancelToken
    };

    return this.instance
      .request(options_)
      .catch((_error: any) => {
        if (isAxiosError(_error) && _error.response) {
          return _error.response;
        } else {
          throw _error;
        }
      })
      .then((_response: AxiosResponse) => {
        return this.processCustomersGET2(_response);
      });
  }

  /**
   * @param body (optional)
   * @return Success
   */
  customersPUT(
    id: number,
    body?: Customer | undefined,
    cancelToken?: CancelToken | undefined
  ): Promise<Customer> {
    let url_ = this.baseUrl + '/api/Customers/{id}';
    if (id === undefined || id === null)
      throw new Error("The parameter 'id' must be defined.");
    url_ = url_.replace('{id}', encodeURIComponent('' + id));
    url_ = url_.replace(/[?&]$/, '');

    const content_ = JSON.stringify(body);

    const options_ = <AxiosRequestConfig>{
      data: content_,
      method: 'PUT',
      url: url_,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      cancelToken
    };

    return this.instance
      .request(options_)
      .catch((_error: any) => {
        if (isAxiosError(_error) && _error.response) {
          return _error.response;
        } else {
          throw _error;
        }
      })
      .then((_response: AxiosResponse) => {
        return this.processCustomersPUT(_response);
      });
  }

  /**
   * @return Success
   */
  customersDELETE(
    id: number,
    cancelToken?: CancelToken | undefined
  ): Promise<void> {
    let url_ = this.baseUrl + '/api/Customers/{id}';
    if (id === undefined || id === null)
      throw new Error("The parameter 'id' must be defined.");
    url_ = url_.replace('{id}', encodeURIComponent('' + id));
    url_ = url_.replace(/[?&]$/, '');

    const options_ = <AxiosRequestConfig>{
      method: 'DELETE',
      url: url_,
      headers: {},
      cancelToken
    };

    return this.instance
      .request(options_)
      .catch((_error: any) => {
        if (isAxiosError(_error) && _error.response) {
          return _error.response;
        } else {
          throw _error;
        }
      })
      .then((_response: AxiosResponse) => {
        return this.processCustomersDELETE(_response);
      });
  }

  protected processCustomersGET(
    response: AxiosResponse
  ): Promise<DataSourceResult> {
    const status = response.status;
    const _headers: any = {};
    if (response.headers && typeof response.headers === 'object') {
      for (const k in response.headers) {
        if (Object.prototype.hasOwnProperty.call(response.headers, k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      const resultData200 = _responseText;
      result200 = resultData200;
      return Promise.resolve<DataSourceResult>(result200);
    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException(
        'An unexpected server error occurred.',
        status,
        _responseText,
        _headers
      );
    }
    return Promise.resolve<DataSourceResult>(<any>null);
  }

  protected processCustomersPOST(response: AxiosResponse): Promise<Customer> {
    const status = response.status;
    const _headers: any = {};
    if (response.headers && typeof response.headers === 'object') {
      for (const k in response.headers) {
        if (Object.prototype.hasOwnProperty.call(response.headers, k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      const resultData200 = _responseText;
      result200 = resultData200;
      return Promise.resolve<Customer>(result200);
    } else if (status === 201) {
      const _responseText = response.data;
      let result201: any = null;
      const resultData201 = _responseText;
      result201 = resultData201;
      return Promise.resolve<Customer>(result201);
    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException(
        'An unexpected server error occurred.',
        status,
        _responseText,
        _headers
      );
    }
    return Promise.resolve<Customer>(<any>null);
  }

  protected processCustomersGET2(response: AxiosResponse): Promise<Customer> {
    const status = response.status;
    const _headers: any = {};
    if (response.headers && typeof response.headers === 'object') {
      for (const k in response.headers) {
        if (Object.prototype.hasOwnProperty.call(response.headers, k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      const resultData200 = _responseText;
      result200 = resultData200;
      return Promise.resolve<Customer>(result200);
    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      const resultData404 = _responseText;
      result404 = resultData404;
      return throwException(
        'Not Found',
        status,
        _responseText,
        _headers,
        result404
      );
    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException(
        'An unexpected server error occurred.',
        status,
        _responseText,
        _headers
      );
    }
    return Promise.resolve<Customer>(<any>null);
  }

  protected processCustomersPUT(response: AxiosResponse): Promise<Customer> {
    const status = response.status;
    const _headers: any = {};
    if (response.headers && typeof response.headers === 'object') {
      for (const k in response.headers) {
        if (Object.prototype.hasOwnProperty.call(response.headers, k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      const resultData200 = _responseText;
      result200 = resultData200;
      return Promise.resolve<Customer>(result200);
    } else if (status === 409) {
      const _responseText = response.data;
      let result409: any = null;
      const resultData409 = _responseText;
      result409 = resultData409;
      return throwException(
        'Conflict',
        status,
        _responseText,
        _headers,
        result409
      );
    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException(
        'An unexpected server error occurred.',
        status,
        _responseText,
        _headers
      );
    }
    return Promise.resolve<Customer>(<any>null);
  }

  protected processCustomersDELETE(response: AxiosResponse): Promise<void> {
    const status = response.status;
    const _headers: any = {};
    if (response.headers && typeof response.headers === 'object') {
      for (const k in response.headers) {
        if (Object.prototype.hasOwnProperty.call(response.headers, k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 204) {
      const _responseText = response.data;
      return Promise.resolve<void>(<any>null);
    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException(
        'An unexpected server error occurred.',
        status,
        _responseText,
        _headers
      );
    }
    return Promise.resolve<void>(<any>null);
  }
}

export const CustomerService = new CustomersClient();
export default CustomerService;
